import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import SimpleFooter from '../components/SimpleFooter';
import ChatFAB from '../components/ChatFAB';
import ContactModal from '../components/ContactModal';
import Loader from '../components/Loader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DestinationDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [destination, setDestination] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [siteSettings, setSiteSettings] = useState({ whatsappNumber: '+919876543210' });

  useEffect(() => {
    // Fetch site settings
    fetch(`${API_URL}/api/site-settings`)
      .then(res => res.json())
      .then(settings => {
        setSiteSettings(settings);
      })
      .catch(err => console.error('Error fetching site settings:', err));

    // Fetch destination
    fetch(`${API_URL}/api/destinations`)
      .then(res => res.json())
      .then(data => {
        const found = data.find(dest => 
          dest.name.toLowerCase().replace(/\s+/g, '-') === slug
        );
        if (found) {
          setDestination(found);
          // Fetch packages for this destination
          fetch(`${API_URL}/api/destinations/${found.id}/packages`)
            .then(res => res.json())
            .then(packagesData => {
              setPackages(packagesData);
              setLoading(false);
            })
            .catch(err => {
              console.error('Error fetching packages:', err);
              setLoading(false);
            });
        } else {
          navigate('/destinations');
        }
      })
      .catch(err => {
        console.error('Error fetching destination:', err);
        setLoading(false);
        navigate('/destinations');
      });
  }, [slug, navigate]);

  if (loading) {
    return (
      <>
        <Header />
        <Loader fullPage={true} />
        <SimpleFooter />
      </>
    );
  }

  if (!destination) {
    return null;
  }

  return (
    <>
      <Header />
      <div className="destination-detail-page">
        <div className="destination-detail-hero" style={{ backgroundImage: `url(${destination.image})` }}>
          <div className="destination-detail-overlay"></div>
          <div className="destination-detail-hero-content">
            <Link to="/destinations" className="back-to-destinations">← All Destinations</Link>
            <h1>{destination.name}</h1>
            <p className="destination-detail-tagline">{destination.country}</p>
          </div>
        </div>

        <div className="destination-detail-container">
          <div className="destination-detail-content">
            <section className="destination-section">
              <h2>About {destination.name}</h2>
              <p className="destination-description">{destination.description}</p>
            </section>

            <section className="destination-section">
              <h2>Available Packages</h2>
              {packages.length > 0 ? (
                <div className="packages-grid">
                  {packages.map(pkg => (
                    <Link key={pkg.id} to={`/package/${pkg.id}`} className="package-card-link">
                      <div className="package-card">
                        <div className="package-image-wrapper">
                          <img src={pkg.image} alt={pkg.title} className="package-image" />
                          {pkg.featured && (
                            <span className="package-featured-badge">
                              <i className="fa-solid fa-star"></i> Featured
                            </span>
                          )}
                        </div>
                        <div className="package-content">
                          <h3 className="package-title">{pkg.title}</h3>
                          <p className="package-duration">
                            <i className="fa-regular fa-clock"></i> {pkg.duration}
                          </p>
                          <p className="package-description">{pkg.description}</p>

                          <div className="package-footer">
                            <div className="package-price">
                              {pkg.originalPrice && (
                                <span className="package-original-price">₹{pkg.originalPrice}</span>
                              )}
                              <span className="package-current-price">₹{pkg.price}</span>
                            </div>
                            <span className="package-view-details">
                              View Details <i className="fa-solid fa-arrow-right"></i>
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="packages-placeholder">
                  <p>No packages available yet for {destination.name}. Contact us to customize your perfect trip!</p>
                </div>
              )}
            </section>

            {destination.highlights && destination.highlights.length > 0 && (
              <section className="destination-section">
                <h2>Highlights</h2>
                <ul className="highlights-list-detail">
                  {destination.highlights.map((highlight, index) => (
                    <li key={index}>
                      <i className="fa-solid fa-circle-check"></i>
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="destination-section cta-section">
              <div className="cta-box">
                <h2>Ready to Explore {destination.name}?</h2>
                <p>Contact us today to customize your perfect {destination.name} experience</p>
                <div className="cta-buttons-detail">
                  <button 
                    className="btn-primary-detail"
                    onClick={() => setShowContactModal(true)}
                  >
                    <i className="fa-solid fa-envelope"></i>
                    Get a Quote
                  </button>
                  <a 
                    href={`https://wa.me/${siteSettings.whatsappNumber.replace('+', '')}?text=Hi, I'm interested in the ${destination.name} package`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary-detail"
                  >
                    <i className="fa-brands fa-whatsapp"></i>
                    WhatsApp Us
                  </a>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <SimpleFooter />
      <ChatFAB />
      {showContactModal && (
        <ContactModal 
          onClose={() => setShowContactModal(false)}
          prefilledDestination={destination.name}
        />
      )}
    </>
  );
};

export default DestinationDetail;
